import { concatLatestFrom } from '@ngrx/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, mergeMap, repeat, switchMap, tap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import * as moment from 'moment/moment';
import { ProjectApiService } from '../../services/project-api.service';
import { NotificationsService } from '../../services/notifications.service';
import { CurrentUserService } from '../../services/current-user.service';
import { ProjectSpendService } from '../../services/project-spend.service';
import { commitmentsActions } from '../commitments/commitments.actions';
import { itemTrackingActions } from '../item-tracking/item-tracking.actions';
import { loadSpends, setProjectStartDate, spendActionTypes } from '../spend/spend.actions';
import { AppState } from '../app-state/index';
import {
  PROJECT_VIEWS,
  VIEW_PROJECT_REFRESH_CAUSE,
} from '../../framework/constants/view-project.constants';
import { USER_MANAGER } from '../../framework/constants/user.constants';
import { ProjectDescriptionsService } from '../../services/project-descriptions.service';
import {
  ProjectDescriptionErrorResponse,
  ProjectDescriptionPayload,
} from '../../pages/webapp/projects/view-project/project-updates/project-updates.types';
import { ReportingService } from '../../services/reporting.service';
import { viewProjectSelectors } from './view-project.selectors';
import { viewProjectActions } from './view-project.actions';

@Injectable()
export class ViewProjectEffects {
  constructor(
    private store: Store<AppState>,
    private actions: Actions,
    private projectService: ProjectApiService,
    private notif: NotificationsService,
    private userService: CurrentUserService,
    private spendService: ProjectSpendService,
    private descriptionsService: ProjectDescriptionsService,
    private reportService: ReportingService,
  ) {}

  // loadProject$ = createEffect(() =>
  //   this.actions.pipe(
  //     ofType(viewProjectActions.selectedProjectChanged),
  //     withLatestFrom(this.store.select(viewProjectSelectors.getViewProjectState)),
  //     switchMap(([action, state]) => {
  //       this.store.dispatch(viewProjectActions.startedLoading());
  //       return this.projectService
  //         .getProjectWithObservable(action.projectId)
  //         .pipe(map((project) => viewProjectActions.projectLoaded({ project })));
  //     }),
  //     catchError((error) => {
  //       this.notif.showError(error?.message || 'An error occurred while loading the project');
  //       return of(viewProjectActions.cancel());
  //     }),
  //   ),
  // );

  viewChanged$ = createEffect(() => {
    return this.actions.pipe(
      ofType(viewProjectActions.viewChanged),
      concatLatestFrom(() => this.userService.data$),
      map(([action, user]) => {
        const isManager = user.type.name === USER_MANAGER;
        if (action.view === PROJECT_VIEWS.DEFAULT) {
          const view = isManager ? PROJECT_VIEWS.PROJECT_SPEND : PROJECT_VIEWS.COMMITMENTS;
          return viewProjectActions.changeView({ view });
        }
        this.reportService.setSelectedProjectView(action.view);
        return viewProjectActions.changeView({ view: action.view });
      }),
    );
  });

  loadTemplateByProperty$ = createEffect(() => {
    return this.actions.pipe(
      ofType(viewProjectActions.loadTemplatesByProperty),
      exhaustMap((action) => {
        return this.projectService.getBudgetTemplates(action.propertyId, true).pipe(
          map((templates) => {
            return viewProjectActions.projectTemplatesLoaded({ templates });
          }),
        );
      }),
    );
  });

  refresh$ = createEffect(() => {
    return this.actions.pipe(
      ofType(viewProjectActions.refreshNeeded, viewProjectActions.selectedProjectChanged),
      tap((action) => console.log('refresh effect', action, performance.now())),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.getViewProjectState)),
      mergeMap(([action, state]) => {
        const projectId = (action as any).projectId ?? state.currentProjectId;
        this.store.dispatch(viewProjectActions.startedLoading({}));
        this.store.dispatch(commitmentsActions.projectChanged({ projectId }));

        if ('cause' in action && action.cause === VIEW_PROJECT_REFRESH_CAUSE.SPEND_SET_ALL) {
          // refresh only the line items, the other data is already loaded
          this.store.dispatch(viewProjectActions.loadLineItems({ projectId }));
          return of(viewProjectActions.cancel());
        }

        if (this.userService.isManager) {
          this.store.dispatch(loadSpends({ projectId }));
          this.store.dispatch(viewProjectActions.loadSpends({ projectId }));
          this.store.dispatch(viewProjectActions.loadLineItems({ projectId }));
          this.store.dispatch(itemTrackingActions.setActiveProject({ projectId }));
        }

        return this.projectService.getProjectWithObservable(state.currentProjectId).pipe(
          catchError((error) => {
            console.warn('Error loading project', error?.error?.message);
            this.notif.showError(
              error?.error?.message || 'An error occurred while loading the project',
            );
            return of(null);
          }),
          tap((project) => {
            if (!project) {
              return;
            }

            this.store.dispatch(
              viewProjectActions.loadTemplatesByProperty({ propertyId: project.property_id }),
            );

            this.store.dispatch(
              commitmentsActions.setCommitmentProjectData({
                projectData: {
                  id: project.id,
                  start_date: project.start_date,
                },
              }),
            );

            if (this.userService.isManager) {
              this.store.dispatch(
                setProjectStartDate({
                  projectStartDate: moment.utc(project.start_date).format('YYYY-MM-DD'),
                }),
              );
              this.store.dispatch(
                spendActionTypes.setHasProjectTemplateFromViewProject({
                  hasTemplates: !!project?.budget_template_id,
                }),
              );
            }
          }),
          map((project) => {
            if (!project) {
              return viewProjectActions.cancel();
            }
            this.notif.close();
            return viewProjectActions.projectLoaded({ project });
          }),
        );
      }),
    );
  });

  loadSpends$ = createEffect(() => {
    return this.actions.pipe(
      ofType(viewProjectActions.loadSpends),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.getViewProjectState)),
      switchMap(([action, state]) => {
        this.store.dispatch(viewProjectActions.startedLoading({ target: 'spends' }));
        const projectId = action.projectId ?? state.currentProjectId;
        return this.spendService.getAllWithObservable(projectId).pipe(
          catchError((error) => {
            this.notif.showError(
              error?.error?.message || 'An error occurred while fetching spend data.',
            );
            this.store.dispatch(viewProjectActions.loadingEnded({ target: 'spends' }));
            console.warn('get all spends for overview error', error);
            return of(undefined);
          }),
          map((spends) => {
            if (!spends) {
              return viewProjectActions.cancel();
            }
            return viewProjectActions.spendsLoaded({ projectId, spends });
          }),
        );
      }),
    );
  });

  DescriptionsSaved$ = createEffect(
    () => {
      return this.actions.pipe(
        ofType(
          viewProjectActions.reloadProjectDescriptions,
          viewProjectActions.projectDescriptionsSuccessfullySaved,
        ),
        tap((_) => this.notif.showSuccess('Successfully saved!')),
      );
    },
    { dispatch: false },
  );

  loadProjectDescriptions$ = createEffect(() => {
    return this.actions.pipe(
      ofType(
        viewProjectActions.loadProjectDescriptions,
        viewProjectActions.reloadProjectDescriptions,
      ),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.getViewProjectState)),
      switchMap(([action, state]) => {
        const projectId = action.projectId ?? state.currentProjectId;
        return this.descriptionsService.getProjectDescriptions(projectId).pipe(
          map((descriptions) => {
            return viewProjectActions.projectDescriptionsLoaded({
              projectDescriptions: descriptions,
              projectId: state.currentProjectId,
            });
          }),
        );
      }),
      catchError((error) => {
        this.notif.showError(
          error?.error?.message || 'An error occurred while project loading updates.',
        );
        console.warn('project loading data err', error);
        return of(viewProjectActions.cancel());
      }),
      repeat(),
    );
  });

  saveProjectDescriptionsAsync$ = createEffect(() => {
    // not the greatest architecture of the world... But we have to cook with the ingredients we have.
    // todo: refactor this whole messy thing and retry failed responses and update store with the OK responses
    return this.actions.pipe(
      ofType(viewProjectActions.saveProjectDescriptionsAsync),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.getViewProjectState)),
      mergeMap(([action, state]) => {
        const updateObservables = action.updatedDescriptions.map((description) => {
          const payload: Partial<ProjectDescriptionPayload> = {
            ...description,
            project_id: state.currentProjectId,
          };
          return this.descriptionsService.updateProjectDescription(payload).pipe(
            catchError((error): Observable<ProjectDescriptionErrorResponse> => {
              return of({
                error,
                description,
                type: 'update',
              });
            }),
          );
        });

        const createObservables = action.newDescriptions.map((description) => {
          const payload: ProjectDescriptionPayload = {
            ...description,
            project_id: state.currentProjectId,
          };
          delete payload.id;
          return this.descriptionsService.createProjectDescription(payload).pipe(
            catchError((error): Observable<ProjectDescriptionErrorResponse> => {
              return of({
                error,
                description,
                type: 'create',
              });
            }),
          );
        });

        const deleteObservables = action.deletedIds.map((id) => {
          return this.descriptionsService
            .deleteProjectDescription(id, {
              project_id: state.currentProjectId,
            })
            .pipe(
              catchError((error): Observable<ProjectDescriptionErrorResponse> => {
                return of({
                  error,
                  description: { id },
                  type: 'delete',
                });
              }),
            );
        });

        return forkJoin([...updateObservables, ...createObservables, ...deleteObservables]).pipe(
          map((responses) => {
            const errorResponses = responses.filter((resp) => resp.error);
            if (errorResponses.length > 0) {
              console.warn('Some responses have errors', errorResponses);
              this.notif.showError(
                `Couldn't save all project descriptions. ${errorResponses[0]?.error?.message || ''}`,
              );
            }
            return viewProjectActions.reloadProjectDescriptions({
              projectId: state.currentProjectId,
            });
          }),
        );
      }),
      repeat(),
    );
  });

  loadLineItems$ = createEffect(() => {
    return this.actions.pipe(
      ofType(viewProjectActions.loadLineItems),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.getViewProjectState)),
      switchMap(([action, state]) => {
        this.store.dispatch(viewProjectActions.startedLoading({ target: 'lineItems' }));
        const projectId = action.projectId ?? state.currentProjectId;
        return this.spendService.getLineItemsByProjectId(projectId, true).pipe(
          catchError((error) => {
            this.notif.showError(
              error?.error?.message || 'An error occurred while fetching line items.',
            );
            this.store.dispatch(viewProjectActions.loadingEnded({ target: 'lineItems' }));
            console.warn('get all line items for overview error', error);
            return of(undefined);
          }),
          map((lineItems) => {
            if (!lineItems) {
              return viewProjectActions.cancel();
            }
            return viewProjectActions.lineItemsLoaded({ projectId, lineItems });
          }),
        );
      }),
    );
  });
}
